import { AnimatePresence, motion } from "motion/react";
import type { Product } from "../../products/hooks/useProducts";
import type { CardPricingRow } from "../../pos/hooks/usePosSession";
import { formatInrTrimmed } from "../../../lib/currency";
import { mapProductToCardView } from "../utils/productViewMappers";
import { ProductCardV2 } from "./ProductCardV2";
import { ProductListItemV2 } from "./ProductListItemV2";
import { Skeleton } from "../../../app/components/ui/skeleton";

type ProductGridV2Props = {
  products: Product[];
  cardPricingByProductId: Record<string, CardPricingRow>;
  productView: "grid" | "list";
  formatQty: (n: number) => string;
  onAddProduct: (productId: string, name: string) => void;
  productsLoading: boolean;
  productsError: string | null;
  /** For subtle list animation when data changes */
  listKey: string;
};

export function ProductGridV2({
  products,
  cardPricingByProductId,
  productView,
  formatQty,
  onAddProduct,
  productsLoading,
  productsError,
  listKey,
}: ProductGridV2Props) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-muted/40 px-3 pb-3 pt-1.5 sm:px-4 sm:pb-4">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {productsError ? (
          <div className="py-12 text-center text-sm text-destructive">{productsError}</div>
        ) : productsLoading ? (
          <div
            className={
              productView === "grid"
                ? "grid grid-cols-2 gap-2.5 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7"
                : "flex flex-col gap-2"
            }
            aria-busy
            aria-label="Loading products"
          >
            {Array.from({ length: productView === "grid" ? 12 : 8 }).map((_, i) => (
              <Skeleton
                key={i}
                className={productView === "grid" ? "aspect-[4/5] rounded-2xl" : "h-16 w-full rounded-xl"}
              />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="flex h-64 items-center justify-center text-muted-foreground">No products found</div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={`${productView}-${listKey}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.15 }}
              className={
                productView === "grid"
                  ? "grid grid-cols-2 gap-2.5 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7"
                  : "flex flex-col gap-1.5"
              }
            >
              {productView === "grid"
                ? products.map((p) => {
                    const model = mapProductToCardView(
                      p,
                      cardPricingByProductId[String(p.id)],
                      formatQty,
                      formatInrTrimmed,
                    );
                    return (
                      <ProductCardV2
                        key={p.id}
                        model={model}
                        onAdd={() => void onAddProduct(String(p.id), p.name)}
                      />
                    );
                  })
                : products.map((p) => {
                    const model = mapProductToCardView(
                      p,
                      cardPricingByProductId[String(p.id)],
                      formatQty,
                      formatInrTrimmed,
                    );
                    return (
                      <ProductListItemV2
                        key={p.id}
                        model={model}
                        onAdd={() => void onAddProduct(String(p.id), p.name)}
                      />
                    );
                  })}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
